import React, { useEffect, useMemo, useRef } from "react";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css";

import { sanitizeRichTextHtml } from "../../helpers/richTextSanitize";

interface QuillFormFieldProps {
  value: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  className?: string;
  editorHeight?: number;
}

const KNOWLEDGE_BASE_TOOLBAR = [["bold"], [{ list: "ordered" }, { list: "bullet" }]];

export const isQuillHtmlEmpty = (html: string): boolean => {
  const text = html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

  return text.length === 0;
};

export const normalizeQuillHtml = (html: string): string | null => {
  const sanitized = sanitizeRichTextHtml(html.trim());

  if (!sanitized || isQuillHtmlEmpty(sanitized)) {
    return null;
  }

  return sanitized;
};

const QuillFormField: React.FC<QuillFormFieldProps> = ({
  value,
  onChange,
  onBlur,
  className = "snow-editor",
  editorHeight = 200,
}) => {
  const modules = useMemo(
    () => ({
      toolbar: KNOWLEDGE_BASE_TOOLBAR,
    }),
    [],
  );

  const { quill, quillRef } = useQuill({ modules, theme: "snow" });
  const isUpdatingRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!quill) {
      return undefined;
    }

    const handleTextChange = () => {
      if (isUpdatingRef.current) {
        return;
      }

      onChangeRef.current(sanitizeRichTextHtml(quill.root.innerHTML));
    };

    const handleBlur = () => {
      onBlur?.();
    };

    quill.on("text-change", handleTextChange);
    quill.root.addEventListener("blur", handleBlur);

    return () => {
      quill.off("text-change", handleTextChange);
      quill.root.removeEventListener("blur", handleBlur);
    };
  }, [quill, onBlur]);

  useEffect(() => {
    if (!quill) {
      return;
    }

    const current = quill.root.innerHTML;
    const next = value ? sanitizeRichTextHtml(value) : "";

    if (current === next || (isQuillHtmlEmpty(current) && isQuillHtmlEmpty(next))) {
      return;
    }

    isUpdatingRef.current = true;
    quill.root.innerHTML = next;
    isUpdatingRef.current = false;
  }, [quill, value]);

  return (
    <div className={className} style={{ height: editorHeight }}>
      <div ref={quillRef} />
    </div>
  );
};

export default QuillFormField;
